import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle, AppText, AppCardTitle, AppSmallText} from '../../../Presentation/components/ui/Typography';
import Button from '../../../Presentation/components/ui/Button';
import {ArrowLeft, Package, AlertCircle} from 'lucide-react';
import {Product} from '../models/product.model';
import {ProductAPIDataSource} from '../api';
import {mapProductDTOToModel} from '../mappers/product.mapper';
import AddStockForm from '../../inventory/components/AddStockForm';
import Spinner from '../../../Presentation/components/ui/Spinner';
import {useNotification} from '../../../Services/useNotification';
import {useAuth} from '../../auth/hooks/useAuth';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

const AddStockPage: React.FC = () => {
  const {productId} = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const {user, loading: authLoading} = useAuth();
  const {hasPermission} = useAuthorization();
  const {success: showSuccess} = useNotification();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId]);

  const loadProduct = async () => {
    if (!productId) {return;}

    setLoading(true);
    setError(null);

    try {
      const response = await ProductAPIDataSource.getById(parseInt(productId));
      const productModel = mapProductDTOToModel(response);
      setProduct(productModel);
    } catch (err) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(err);
      setError(appError.message);
      errorHandlingService.handleError(err); // For notifications/logging
    } finally {
      setLoading(false);
    }
  };

  const handleAddStockSuccess = () => {
    showSuccess('تم إضافة المخزون بنجاح');
    navigate('/dashboard/products');
  };

  const handleCancel = () => {
    navigate('/dashboard/products');
  };

  if (loading || authLoading) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size={40} />
        </div>
      </SidebarLayout>
    );
  }

  // Error is handled by StandardizedErrorHandler and shown as toast notification
  if (!product) {
    return (
      <SidebarLayout>
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <AppCardTitle className="text-red-600 mb-2">
            المنتج غير موجود
          </AppCardTitle>
          <Button
            onClick={handleCancel}
            variant="outline"
            className="border-brand-600 text-brand-600 hover:bg-brand-50"
          >
            <ArrowLeft size={16} className="mr-2" />
            العودة للمنتجات
          </Button>
        </div>
      </SidebarLayout>
    );
  }

  // Check if user has permission to add inventory
  if (!hasPermission(PERMISSIONS.CREATE_INVENTORY) &&
      !hasPermission(PERMISSIONS.MANAGE_INVENTORY)) {
    return (
      <SidebarLayout>
        <div className="p-6">
          <PermissionDeniedCard
            title="غير مصرح لك بإضافة المخزون"
            message="ليس لديك صلاحيات لإضافة المخزون. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة."
          />
          <div className="text-center mt-4">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="border-brand-600 text-brand-600 hover:bg-brand-50"
            >
              <ArrowLeft size={16} className="mr-2" />
              العودة للمنتجات
            </Button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  // Ensure user is authenticated (secondary check)
  if (!user || !user.id) {
    return (
      <SidebarLayout>
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <AppCardTitle className="text-red-600 mb-2">
            يجب تسجيل الدخول لإضافة المخزون
          </AppCardTitle>
          <Button
            onClick={handleCancel}
            variant="outline"
            className="border-brand-600 text-brand-600 hover:bg-brand-50"
          >
            <ArrowLeft size={16} className="mr-2" />
            العودة للمنتجات
          </Button>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleCancel}
              variant="ghost"
              className="flex items-center gap-2 hover:bg-gray-100"
            >
              <ArrowLeft size={20} />
              العودة
            </Button>
            <div>
              <AppTitle className="text-2xl">إضافة مخزون</AppTitle>
              <AppText className="text-gray-600 mt-1">
                إضافة مخزون للمنتج: {product.name}
              </AppText>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-brand-50 px-4 py-2 rounded-lg">
            <Package size={20} className="text-brand-600" />
            <div>
              <AppText className="font-medium text-brand-900">{product.name}</AppText>
              <AppSmallText className="text-brand-600">{product.barcode}</AppSmallText>
            </div>
          </div>
        </div>

        {/* Add Stock Form */}
        <AddStockForm
          product={product}
          currentUserId={user.id}
          onSuccess={handleAddStockSuccess}
          onCancel={handleCancel}
        />
      </div>
    </SidebarLayout>
  );
};

export default AddStockPage;
